import { useBreakpointIndex } from '@apia/theme';
import { EventEmitter } from '@apia/util';
import React from 'react';

export interface IMenuDefinition {
  menu: React.ReactNode;
  stage: string;
}

type TMenuEvents = {
  updateMenu: React.ReactNode;
};

class MenuController extends EventEmitter<TMenuEvents> {
  #currentStage = '';

  #menues: IMenuDefinition[] = [];

  on<K extends keyof TMenuEvents>(
    eventName: K,
    fn: (ev: TMenuEvents[K]) => unknown,
  ) {
    super.on(eventName, fn);
    const currentMenu = this.#menues.find(
      (current) => current.stage === this.#currentStage,
    )?.menu;
    if (currentMenu) fn(currentMenu);
    return () => this.off(eventName, fn);
  }

  getCurrent() {
    return (
      this.#menues.find((current) => current.stage === this.#currentStage)
        ?.menu ?? null
    );
  }

  remove(stage: string) {
    this.#menues = this.#menues.filter((current) => current.stage !== stage);

    if (stage === this.#currentStage) {
      this.#currentStage = this.#menues[this.#menues.length - 1]?.stage ?? null;
      this.#shout();
    }
  }

  setMenu(definition: IMenuDefinition) {
    this.#menues.push(definition);
    this.#currentStage = definition.stage;
    this.#shout();
    return () => {
      this.remove(definition.stage);
    };
  }

  #shout() {
    this.emit(
      'updateMenu',
      this.#menues.find((current) => current.stage === this.#currentStage)
        ?.menu,
    );
  }
}

/**
 * El menú controller pretende solucionar el problema de tener múltiples menúes
 * para distintas pantallas dentro del mismo flujo de trabajo. El problema
 * original surge en administración de grupos. Al presionar modificar se
 * cambia de pantalla y por lo tanto de menú, pero se sigue estando en el mismo
 * flujo y se pretende que al presionar regresar, se restaure el menú original.
 * Todo esto sin afectar la responsividad de los mismos.
 *
 * Para solucionar esta problemática, se introduce este controlador, cuyo
 * funcionamiento es muy sencillo. Cada vez que un componente quiere setear un
 * menú para una pantalla particular, hace uso del método
 * menuController.setMenu.
 *
 * Una vez que la pantalla se está desmontando, debe desmontar su menú haciendo
 * uso del método menuController.remove.
 *
 * Mediante el hook useMenu provisto en este documento, se puede hacer uso del
 * menú actual, sin preocuparse de la existencia de varios elementos que
 * coincidan.
 */
export const menuController = new MenuController();

/**
 * Este hook permite insertar el menú en un componente.
 */
export function useMenu(breakPoints?: number[], initialMenu?: IMenuDefinition) {
  const [menu, setMenu] = React.useState<React.ReactNode>(null);
  const currentBreakpoint = useBreakpointIndex();

  React.useEffect(() => {
    const unsuscribe = menuController.on('updateMenu', (newMenu) => {
      setMenu(newMenu);
    });

    if (initialMenu) menuController.setMenu(initialMenu);

    return () => {
      unsuscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (breakPoints && !breakPoints.includes(currentBreakpoint)) return null;

  return menu;
}
